import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authFetch, setAccessToken, BASE_BACKEND_URL } from '../../auth'
import { useAuth } from '../../auth/AuthContext'
import { saveSessionCookies } from '../../auth/sessionCookies'
import { TIMED_TOAST_ALERT_DURATION_MS } from '../../components/alerts/TimedToastAlert'
import { GlobalContext } from '../../global_context/GlobalContext'
import { setUpTaxSettings } from '../../helpers/utils'
import {
  getLoginAlertMessage,
  getLoginErrorState,
  isLoginToastState,
  LOGIN_ALERTS,
  LoginState
} from '../constants/loginStates'
import { saveMfaToken } from '../mfaStorage'
import { isValidLoginEmail, LOGIN_FIELD_ERRORS } from '../shared/loginConstants'

export default function useLoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginState, setLoginState] = useState(LoginState.NONE)
  const [alertId, setAlertId] = useState(0)
  const [alertAsToast, setAlertAsToast] = useState(false)
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const { applyLoginPayload, isAuthenticated, isBootstrapping } = useAuth()
  const { syncUserRole } = useContext(GlobalContext)
  const navigate = useNavigate()

  const showLoginAlert = (nextState, { asToast } = {}) => {
    setLoginState(nextState)
    setAlertAsToast(asToast ?? isLoginToastState(nextState))
    setAlertId(Date.now())
  }

  const clearLoginAlert = () => {
    setLoginState(LoginState.NONE)
    setAlertAsToast(false)
  }

  const handleEmailChange = (value) => {
    setEmail(value)
    if (emailError) setEmailError('')
    if (loginState !== LoginState.NONE && !alertAsToast) {
      clearLoginAlert()
    }
  }

  const handlePasswordChange = (value) => {
    setPassword(value)
    if (passwordError) setPasswordError('')
    if (loginState !== LoginState.NONE && !alertAsToast) {
      clearLoginAlert()
    }
  }

  const validateCredentials = () => {
    const trimmedEmail = email.trim()
    let nextEmailError = ''
    let nextPasswordError = ''

    if (!trimmedEmail) {
      nextEmailError = LOGIN_FIELD_ERRORS.emailRequired
    } else if (!isValidLoginEmail(trimmedEmail)) {
      nextEmailError = LOGIN_FIELD_ERRORS.emailInvalid
    }

    if (!password) {
      nextPasswordError = LOGIN_FIELD_ERRORS.passwordRequired
    }

    setEmailError(nextEmailError)
    setPasswordError(nextPasswordError)

    return !nextEmailError && !nextPasswordError
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const asToast = { asToast: true }

    if (params.get('logout') === 'true') showLoginAlert(LoginState.LOGGED_OUT, asToast)
    if (params.get('reset_done') === 'true') showLoginAlert(LoginState.RESET_PASSWORD, asToast)
    if (params.get('deactivated') === 'true') showLoginAlert(LoginState.USER_DEACTIVATED, asToast)
    if (params.get('session_expired') === 'true') showLoginAlert(LoginState.SESSION_EXPIRED, asToast)
    if (params.get('session_replaced') === 'true') showLoginAlert(LoginState.SESSION_REPLACED, asToast)
    if (params.get('account_locked') === 'true') showLoginAlert(LoginState.ACCOUNT_LOCKED, asToast)
    if (params.get('session_error') === 'true') showLoginAlert(LoginState.GENERIC_ERROR, asToast)
  }, [])

  useEffect(() => {
    if (loginState === LoginState.NONE) return undefined

    const dismissTimer = window.setTimeout(() => {
      setLoginState(LoginState.NONE)
    }, TIMED_TOAST_ALERT_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [loginState, alertId])

  useEffect(() => {
    if (isBootstrapping) return
    if (!isAuthenticated) return
    if (window.location.pathname !== '/') return

    authFetch(`${BASE_BACKEND_URL}/users/current`)
      .then((response) => {
        if (!response?.ok) throw new Error('Failed to fetch user data')
        return response.json()
      })
      .then((data) => {
        if (data) navigate('/inicio', { replace: true })
      })
      .catch(console.error)
  }, [navigate, isAuthenticated, isBootstrapping])

  const evaluateResponse = (json) => {
    if (!json.token) return false

    setAccessToken(json.token)
    applyLoginPayload(json)
    saveSessionCookies(json, { email: email.trim() })
    syncUserRole?.()
    setUpTaxSettings(json.intl_locale)
    navigate('/inicio', { replace: true })
    return true
  }

  const login = (event) => {
    event.preventDefault()
    if (!validateCredentials()) return

    setIsSubmitting(true)

    fetch(`${BASE_BACKEND_URL}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ user: { email: email.trim(), password } })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setRetryAfterSeconds(data.retry_after || null)
          showLoginAlert(getLoginErrorState(response.status, data.reason))
          return
        }

        if (data.mfa_required && data.mfa_token) {
          saveMfaToken({
            mfa_token: data.mfa_token,
            mfa_method: data.mfa_method || null,
            mfa_methods: Array.isArray(data.mfa_methods) ? data.mfa_methods : [],
            destination_hint: data.destination_hint || '',
            remember_device_available: Boolean(data.remember_device_available),
            remember_device_days: data.remember_device_days || 0
          })
          navigate('/login/mfa', { replace: true })
          return
        }

        if (!evaluateResponse(data)) {
          showLoginAlert(LoginState.GENERIC_ERROR)
        }
      })
      .catch(() => {
        showLoginAlert(LoginState.GENERIC_ERROR)
      })
      .finally(() => setIsSubmitting(false))
  }

  const alertMessage = getLoginAlertMessage(loginState, retryAfterSeconds)
  const alertSeverity = LOGIN_ALERTS[loginState]?.variant || 'error'
  const isToastAlert = Boolean(alertMessage) && alertAsToast

  return {
    email,
    setEmail: handleEmailChange,
    password,
    setPassword: handlePasswordChange,
    emailError,
    passwordError,
    showPassword,
    setShowPassword,
    isSubmitting,
    login,
    alertId,
    formAlertMessage: isToastAlert ? null : alertMessage,
    formAlertSeverity: isToastAlert ? 'error' : alertSeverity,
    toastAlertMessage: isToastAlert ? alertMessage : null,
    toastAlertSeverity: isToastAlert ? alertSeverity : 'error',
    clearLoginAlert
  }
}
